<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="emit('changeOpen', false)">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        :enter-from="transitionClasses.enterFrom"
        :enter-to="transitionClasses.enterTo"
        leave="ease-in-out duration-500"
        :leave-from="transitionClasses.leaveFrom"
        :leave-to="transitionClasses.leaveTo"
      >
        <div class="fixed inset-0 bg-black/50 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div :class="transitionClasses.panelPosition">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              :enter-from="transitionClasses.enterFrom"
              :enter-to="transitionClasses.enterTo"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              :leave-from="transitionClasses.leaveFrom"
              :leave-to="transitionClasses.leaveTo"
            >
              <DialogPanel :class="transitionClasses.panelSize">
                <div
                  class="flex h-full"
                  :class="[openLeft === true ? 'flex-row' : 'flex-col bg-gray-50']"
                >
                  <div
                    v-if="fromSide === true && Capacitor.getPlatform() === 'ios'"
                    class="notch-padding"
                  ></div>
                  <div
                    v-if="openLeft === false"
                    class="bg-gray-50 py-6"
                    :class="[
                      'flex items-start',
                      title !== '' ? 'justify-between' : 'justify-end',
                      largeDrawer === true
                        ? 'border-b border-gray-200 px-4 sm:px-10'
                        : 'px-4 sm:px-6'
                    ]"
                  >
                    <div class="flex w-full items-center justify-between space-x-3">
                      <div class="space-y-1">
                        <DialogTitle
                          v-if="title !== ''"
                          class="flex items-center text-base font-semibold leading-6 text-gray-900"
                        >
                          <template v-if="subDrawer === true">
                            <div class="mr-4 flex h-10 w-10 items-center justify-start">
                              <font-awesome-icon
                                :icon="['fal', 'angle-left']"
                                class="gray-500 mr-0 h-6 cursor-pointer"
                                @click="emit('closeSubDrawer')"
                              />
                            </div>
                            <div class="items-center justify-center">{{ title }}</div>
                          </template>
                          <template v-else>
                            <AvatarComponent
                              v-if="avatarSettings !== undefined"
                              :icon="avatarSettings.icon"
                              :color="avatarSettings.color"
                              border-color="border border-stone-200"
                              avatar-class="mr-4 h-10 w-10"
                              icon-class="h-5 w-5"
                            />
                            {{ title }}
                          </template>
                        </DialogTitle>
                      </div>
                      <div v-if="subDrawer === false" class="ml-3 flex h-7 items-center">
                        <slot name="headerIcon"></slot>
                        <ButtonComponent
                          :icon="['fal', 'close']"
                          screen-reader-text="Close panel"
                          type="transparent"
                          size="sm"
                          @clicked="emit('changeOpen', false)"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    id="drawerContent"
                    class="flex h-full w-full flex-col overflow-y-auto bg-white"
                  >
                    <div :class="[largeDrawer === true ? 'px-4 sm:px-10' : 'px-4 sm:px-6']"></div>
                    <div :class="['relative flex-1', largeDrawer === true ? 'px-4 sm:px-10' : '']">
                      <slot name="content"></slot>
                    </div>
                  </div>
                  <!-- Because of the notch on IOS we need to push the X (Close icon) down on the screen so its not placed inside the notch, thats why we have extra padding for IOS only. -->
                  <!-- One of the examples is the Navigation Menu -->
                  <div
                    v-if="openLeft === true"
                    :class="Capacitor.getPlatform() === 'ios' ? 'px-4 py-2' : 'p-4'"
                    @click="emit('changeOpen', false)"
                  >
                    <div v-if="Capacitor.getPlatform() === 'ios'" class="notch-padding"></div>
                    <font-awesome-icon
                      size="2xl"
                      class="fatshrink-0 w-7 cursor-pointer !text-white"
                      aria-hidden="true"
                      :icon="['fal', 'circle-xmark']"
                      aria-label="close menu"
                      @click="emit('changeOpen', false)"
                    />
                  </div>

                  <div class="bg-white">
                    <AlertComponent
                      v-if="showAlert"
                      class="m-4 !w-auto"
                      :class="[largeDrawer === true ? 'sm:mx-10' : 'sm:mx-6']"
                      :title="$t('General_Validating_Missing_Fields')"
                      type="danger"
                      :dismissable="true"
                      @dismiss="drawerStore.setShowAlert(false)"
                    />
                    <div
                      v-if="largeDrawer === true"
                      id="drawerBottom"
                      class="flex min-h-[72px] shrink-0 justify-end border-t border-gray-200 px-4 py-4 sm:px-10"
                    >
                      <!-- This DIV is made to place footer stuff inside - so please dont remove it as it is used -->
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script setup lang="ts">
import type { IAvatarComponent } from '@/src/interfaces/components/IAvatarComponent'
import { useDrawerStore } from '@/stores'
import { Capacitor } from '@capacitor/core'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { storeToRefs } from 'pinia'

interface IDrawerComponent {
  fromSide?: boolean
  title?: string
  open: boolean
  openLeft?: boolean
  largeDrawer?: boolean
  avatarSettings?: IAvatarComponent
  subDrawer?: boolean
}

const drawerStore = useDrawerStore()

const { showAlert } = storeToRefs(drawerStore)

const emit = defineEmits<{
  changeOpen: [value: boolean]
  closeSubDrawer: []
}>()

const {
  fromSide = true,
  title = '',
  openLeft = false,
  largeDrawer = false,
  avatarSettings = undefined,
  subDrawer = false
} = defineProps<IDrawerComponent>()

const transitionClasses = {
  enterFrom: fromSide ? (openLeft ? '-translate-x-full' : 'translate-x-full') : 'translate-y-full',
  enterTo: fromSide ? 'translate-x-0' : 'translate-y-0',
  leaveFrom: fromSide ? 'translate-x-0' : 'translate-y-0',
  leaveTo: fromSide ? (openLeft ? '-translate-x-full' : 'translate-x-full') : 'translate-y-full',
  panelPosition: fromSide
    ? openLeft
      ? 'pointer-events-none fixed inset-y-0 left-0 flex max-w-full'
      : 'pointer-events-none fixed inset-y-0 right-0 flex max-w-full'
    : 'pointer-events-none fixed inset-x-0 bottom-0 flex max-h-full',
  panelSize: fromSide
    ? `pointer-events-auto w-screen ${largeDrawer ? 'max-w-4xl' : 'max-w-md'}`
    : 'max-h-3/4 pointer-events-auto w-full'
}
</script>
